/** @prettier */

import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Grid,
  AppBar,
  Toolbar,
  Stack,
  Box,
  Paper,
  Button,
  Slide,
  alpha,
} from '@mui/material';
import {TransitionProps} from '@mui/material/transitions';

//interface import
import * as apiIF from '../../Type/api';

import {iconWhite, iconYellow} from '../../define';
import {invertComma} from '../../functions/convertString';

/**
 * エラー一覧開閉時のアニメーション
 */
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

/**
 * エラー一覧ヘッダー
 * @param props props
 * @returns JSX.Element
 */
const HeaderErrorList = (props: {
  /** HCCV(Q)、Air Conditionerで異常となっている空調機の状態値 */
  errorState: apiIF.res.getStatesErrorSites['error-informations'];
  /** HCCV(Q)、Air Conditionerで異常となっている空調機の台数 */
  errorNum: number;
  /** HCCV(Q)エラー時にヘッダーに表示するタイトル */
  hccvqErrorTitle: string;
  /** Air Conditionerエラーがある時にヘッダーに表示するタイトル */
  airConditionerErrorTitle: string;
}) => {
  const trans = useTranslation().t; //翻訳関数

  const [isOpenDialog, setIsOpenDialog] = useState(false); //ダイアログ表示ボタン押下フラグ

  /**
   * エラー一覧ダイアログ表示ボタン押下時処理
   * @param event
   */
  const handleClickOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsOpenDialog(prev => !prev);
  };

  /**
   * 実温度と設定温度の差を算出
   * @param actualTemp 実温度
   * @param setTemp 設定温度
   * @returns 差
   */
  const getDifferenceValue = (actualTemp: string, setTemp: string) => {
    const differenceTemp = Number(actualTemp) - Number(setTemp);

    if (differenceTemp < 0) {
      //差が0°C未満
      return differenceTemp.toFixed(1);
    } else {
      //差が0°C以上
      return `+${differenceTemp.toFixed(1)}`;
    }
  };

  /**
   * タイトルエラーアイコン
   * @returns JSX
   */
  const TitleErrorIcon = () => {
    return (
      <img
        className="headerErrorList__header__title__icon"
        src={iconWhite.ERROR.src}
        alt={`${trans(
          `HeaderErrorList.image.alternativeText.${iconWhite.ERROR.alt}`
        )}`}
      />
    );
  };

  /**
   * タイトルを表示
   * @param props props
   * @returns JSX.Element
   */
  const ListTitle = (props: {
    /** HCCV(Q)エラー時にヘッダーに表示するタイトル */
    hccvqTitle: string;
    /** Air Conditionerエラーがある時にヘッダーに表示するタイトル */
    airConditionerTitle: string;
    /** HCCV(Q)、Air Conditionerで異常となっている空調機の台数 */
    errorNum: number;
    /** ダイアログ開閉ボタンに表示するアイコン */
    icon: React.ReactNode;
  }) => {
    /**
     * 表示するタイトル
     * 優先順位：冷媒漏洩 > HCCVQユニットクーラ > HCCVQコンデンシングユニット
     */
    let displayTitle = '';
    if (props.airConditionerTitle !== '') {
      //prettier-ignore
      displayTitle = `${trans('HeaderErrorList.unitType.sl')}: ${trans('HeaderErrorList.errType.refrigerantLeakage')}`;
    } else if (props.hccvqTitle === 'iuError') {
      //prettier-ignore
      displayTitle = `${trans('HeaderErrorList.unitType.hccvq')}: ${trans('HeaderErrorList.errType.temperatureAnomaly')}`;
    } else if (props.hccvqTitle !== '' && props.hccvqTitle !== 'iuError') {
      //prettier-ignore
      displayTitle = `${trans('HeaderErrorList.unitType.hccvq')}: ${props.hccvqTitle} ${trans('HeaderErrorList.unitType.error')}`;
    }
    return (
      <Grid container className="headerErrorList__header">
        <Grid item xs className="headerErrorList__header__title">
          {/* タイトル */}
          <TitleErrorIcon />
          {displayTitle}
        </Grid>
        {/* エラー一覧ダイアログ開閉ボタン */}
        <Grid item xs="auto">
          <Button
            className="headerErrorList__header__button"
            endIcon={props.icon}
            onClick={handleClickOpen}>
            <Box className="headerErrorList__header__button__text">
              {`${props.errorNum} ${trans('HeaderErrorList.unit')}`}
            </Box>
          </Button>
        </Grid>
      </Grid>
    );
  };

  /**
   * HCCVQ室内機エラーを表示
   * @param props props
   * @returns JSX.Element
   */
  const IUErrorList = (props: {
    /** サイトの名前 */
    siteName: string;
    /** Hyozan or HCCVQの室内機エラー状態値 */
    iuErrorList:
      | apiIF.resParameters.hccvqIndoorStatesError[]
      | apiIF.resParameters.hyozanIndoorStatesError[];
  }) => {
    //prettier-ignore
    /** タイトル */
    const title = `${trans('HeaderErrorList.unitType.hccvq')}: ${trans('HeaderErrorList.errType.temperatureAnomaly')}`;

    return (
      <Paper elevation={1}>
        <Stack>
          {/** タイトル */}
          <Box className="headerErrorList__body__errorList__title">
            <Stack>
              <Box className="headerErrorList__body__errorList__title__label">
                {props.siteName}
              </Box>
              <Box className="headerErrorList__body__errorList__title__value">
                {title}
              </Box>
            </Stack>
          </Box>
          {/** 値 */}
          <Box className="headerErrorList__body__errorList__list">
            {props.iuErrorList.map(iuError => {
              //prettier-ignore
              /** 実温度 */
              const insideTemp = `${invertComma(iuError['inside-temperature'])}${trans('HeaderErrorList.list.unit.temp')}`;
              //prettier-ignore
              /** 設定温度 */
              const presetTemp = `${invertComma(iuError['preset-temperature'])}${trans('HeaderErrorList.list.unit.temp')}`;
              //prettier-ignore
              /** 温度差 */
              const differenceTemp = `${invertComma(getDifferenceValue(iuError['inside-temperature'], iuError['preset-temperature']))}${trans('HeaderErrorList.list.unit.temp')}`;

              return (
                <Grid
                  container
                  alignItems="center"
                  columnSpacing={1}
                  key={iuError['indoor-id']}
                  className="headerErrorList__body__errorList__list__row--iu">
                  {/**エラーアイコン */}
                  <Grid item xs={1}>
                    <img
                      src={iconYellow.ERROR.src}
                      alt={`${trans(
                        `HeaderErrorList.image.alternativeText.${iconYellow.ERROR.alt}`
                      )}`}
                      className="headerErrorList__body__errorList__list__row__icon"
                    />
                  </Grid>
                  {/**グループ名、室内機名 */}
                  <Grid
                    item
                    xs={4}
                    className="headerErrorList__body__errorList__list__row__data">
                    <Stack>
                      <Box
                        className="headerErrorList__body__errorList__list__row__label"
                        title={iuError['group-name']}>
                        {iuError['group-name']}
                      </Box>
                      <Box
                        className="headerErrorList__body__errorList__list__row__value"
                        title={iuError['indoor-unit-name']}>
                        {iuError['indoor-unit-name']}
                      </Box>
                    </Stack>
                  </Grid>
                  {/**実温度 */}
                  <Grid item xs>
                    <Stack>
                      <Box className="headerErrorList__body__errorList__list__row__label">
                        {trans('HeaderErrorList.list.label.actualTemp')}
                      </Box>
                      <Box className="headerErrorList__body__errorList__list__row__value">
                        {insideTemp}
                      </Box>
                    </Stack>
                  </Grid>
                  {/**設定温度 */}
                  <Grid item xs>
                    <Stack>
                      <Box className="headerErrorList__body__errorList__list__row__label">
                        {trans('HeaderErrorList.list.label.setTemp')}
                      </Box>
                      <Box className="headerErrorList__body__errorList__list__row__value">
                        {presetTemp}
                      </Box>
                    </Stack>
                  </Grid>
                  {/** 温度差 */}
                  <Grid
                    item
                    xs="auto"
                    className="headerErrorList__body__errorList__list__row__difference">
                    {differenceTemp}
                  </Grid>
                </Grid>
              );
            })}
          </Box>
          {/** メッセージ */}
          <Box className="headerErrorList__body__errorList__message">
            {trans('HeaderErrorList.list.message.temperatureAnomaly')}
          </Box>
        </Stack>
      </Paper>
    );
  };

  /**
   * HCCVQ室外機エラーを表示
   * @param props props
   * @returns JSX.Element
   */
  const OUErrorList = (props: {
    /** サイトの名前 */
    siteName: string;
    /** Hyozan or HCCVQ室外機のエラー状態値 */
    ouErrorList:
      | apiIF.resParameters.hccvqOutdoorStatesError[]
      | apiIF.resParameters.hyozanOutdoorStatesError[];
  }) => {
    return (
      <>
        {props.ouErrorList.map(ouError => {
          //prettier-ignore
          /** タイトル */
          const title = `${trans('HeaderErrorList.unitType.hccvq')}: ${ouError['outdoor-unit-name']} ${trans('HeaderErrorList.error')}`;
          //prettier-ignore
          /** エラーコード */
          const errorCode = `${trans('HeaderErrorList.list.unit.errCode')}${ouError['error-code']}`;

          return (
            <Paper elevation={1} key={ouError['outdoor-id']}>
              <Stack>
                {/** タイトル */}
                <Box className="headerErrorList__body__errorList__title">
                  <Stack>
                    <Box className="headerErrorList__body__errorList__title__label">
                      {props.siteName}
                    </Box>
                    <Box className="headerErrorList__body__errorList__title__value">
                      {title}
                    </Box>
                  </Stack>
                </Box>
                {/** 値 */}
                <Box className="headerErrorList__body__errorList__list">
                  <Grid
                    container
                    alignItems="center"
                    columnSpacing={1}
                    className="headerErrorList__body__errorList__list__row--ou">
                    {/**エラーアイコン */}
                    <Grid item xs="auto">
                      <img
                        src={iconYellow.ERROR.src}
                        alt={`${trans(
                          `HeaderErrorList.image.alternativeText.${iconYellow.ERROR.alt}`
                        )}`}
                        className="headerErrorList__body__errorList__list__row__icon"
                      />
                    </Grid>
                    {/** エラーコード */}
                    <Grid
                      item
                      xs
                      className="headerErrorList__body__errorList__list__row__value">
                      {errorCode}
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </>
    );
  };

  /**
   * フロア名変換テーブル
   * @param floor フロア
   * @returns フロア名
   */
  const convertFloorName = (floor: string) => {
    switch (floor) {
      case '14':
        return trans('HeaderErrorList.floor.floor14');
      case '13':
        return trans('HeaderErrorList.floor.floor13');
      case '12':
        return trans('HeaderErrorList.floor.floor12');
      case '11':
        return trans('HeaderErrorList.floor.floor11');
      case '10':
        return trans('HeaderErrorList.floor.floor10');
      case '9':
        return trans('HeaderErrorList.floor.floor9');
      case '8':
        return trans('HeaderErrorList.floor.floor8');
      case '7':
        return trans('HeaderErrorList.floor.floor7');
      case '6':
        return trans('HeaderErrorList.floor.floor6');
      case '5':
        return trans('HeaderErrorList.floor.floor5');
      case '4':
        return trans('HeaderErrorList.floor.floor4');
      case '3':
        return trans('HeaderErrorList.floor.floor3');
      case '2':
        return trans('HeaderErrorList.floor.floor2');
      case '1':
        return trans('HeaderErrorList.floor.floor1');
      case '0':
        return trans('HeaderErrorList.floor.floor0');
      case '-1':
        return trans('HeaderErrorList.floor.basement1');
      case '-2':
        return trans('HeaderErrorList.floor.basement2');
      case '-3':
        return trans('HeaderErrorList.floor.basement3');
      case '-4':
        return trans('HeaderErrorList.floor.basement4');
      default:
        return '';
    }
  };

  /**
   * AirConditioner室内機エラーを表示
   * @param props props
   * @returns JSX.Element
   */
  const AirConditionerErrorList = (props: {
    /** サイトの名前 */
    siteName: string;
    /** AirConditioner室内機のエラー状態値 */
    airConditionerErrorList: apiIF.resParameters.airConditionerStatesError[];
  }) => {
    return (
      <>
        {props.airConditionerErrorList.map(airConditionerError => {
          //prettier-ignore
          /** タイトル */
          const title = `${trans('HeaderErrorList.unitType.sl')} : ${airConditionerError['indoor-unit-name']}`;
          //prettier-ignore
          /** エラーコード */
          const errorCode = `${trans('HeaderErrorList.list.unit.errCode')}${airConditionerError['error-code']}`;

          return (
            <Paper elevation={1} key={airConditionerError['indoor-id']}>
              <Stack>
                {/** タイトル */}
                <Box className="headerErrorList__body__errorList__title">
                  <Stack>
                    <Box className="headerErrorList__body__errorList__title__label">
                      {props.siteName}
                    </Box>
                    <Box className="headerErrorList__body__errorList__title__value">
                      {title}
                    </Box>
                  </Stack>
                </Box>
                {/** 値 */}
                <Box className="headerErrorList__body__errorList__list">
                  <Grid
                    container
                    alignItems="center"
                    columnSpacing={1}
                    className="headerErrorList__body__errorList__list__row--ou">
                    {/**エラーアイコン */}
                    <Grid item xs="auto">
                      <img
                        src={iconYellow.ERROR.src}
                        alt={`${trans(
                          `HeaderErrorList.image.alternativeText.${iconYellow.ERROR.alt}`
                        )}`}
                        className="headerErrorList__body__errorList__list__row__icon"
                      />
                    </Grid>
                    <Grid item xs>
                      <Box>
                        {`${convertFloorName(airConditionerError['floor'])}, ${
                          airConditionerError['group-name']
                        }`}
                      </Box>
                      <Box>{airConditionerError['indoor-unit-name']}</Box>
                    </Grid>
                    {/** エラーコード */}
                    <Grid
                      item
                      xs
                      className="headerErrorList__body__errorList__list__row__value">
                      {errorCode}
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </>
    );
  };

  return (
    <>
      {/** ダイアログが閉じている時のタイトル */}
      <ListTitle
        hccvqTitle={props.hccvqErrorTitle}
        airConditionerTitle={props.airConditionerErrorTitle}
        errorNum={props.errorNum}
        icon={
          <img
            className="headerErrorList__header__button__icon--open"
            src={iconWhite.CHEVRON_DOWN.src}
            alt={`${trans(
              `HeaderErrorList.image.alternativeText.${iconWhite.CHEVRON_DOWN.alt}`
            )}`}
          />
        }
      />
      <Dialog
        open={isOpenDialog}
        aria-labelledby="hccvq-error-dialog-title"
        fullScreen
        onClose={() => setIsOpenDialog(false)}
        scroll="body"
        TransitionComponent={Transition}
        className="headerErrorList"
        PaperProps={{
          style: {
            // 背景を透明にする
            backgroundColor: alpha('#000', 0.0),
          },
        }}>
        {/** ダイアログが開いている時のタイトル */}
        <AppBar position="static">
          <Toolbar disableGutters>
            <ListTitle
              hccvqTitle={props.hccvqErrorTitle}
              airConditionerTitle={props.airConditionerErrorTitle}
              errorNum={props.errorNum}
              //以下のアイコンはスタイルシートで180度回転させている
              icon={
                <img
                  className="headerErrorList__header__button__icon--close"
                  src={iconWhite.CHEVRON_DOWN.src}
                  alt={`${trans(
                    'HeaderErrorList.image.alternativeText.chevronUp'
                  )}`}
                />
              }
            />
          </Toolbar>
        </AppBar>
        <DialogContent className="headerErrorList__body">
          <Stack spacing={1} className="headerErrorList__body__errorList">
            {props.errorState.map(siteError => (
              <React.Fragment key={`airconditioner_${siteError['site-id']}`}>
                {/** airConditioner室内機エラー */}
                {siteError.airconditioner !== undefined &&
                siteError.airconditioner.length > 0 ? (
                  <AirConditionerErrorList
                    siteName={siteError['site-name']}
                    airConditionerErrorList={siteError.airconditioner}
                  />
                ) : (
                  <></>
                )}
              </React.Fragment>
            ))}
            {props.errorState.map(siteError => (
              <React.Fragment key={`hyozanindoor_${siteError['site-id']}`}>
                {/** Hyozan室内機エラー */}
                {siteError.hyozan['indoor-unit-error-states'] !== undefined &&
                siteError.hyozan['indoor-unit-error-states'].length > 0 ? (
                  <IUErrorList
                    siteName={siteError['site-name']}
                    iuErrorList={siteError.hyozan['indoor-unit-error-states']}
                  />
                ) : (
                  <></>
                )}
              </React.Fragment>
            ))}
            {props.errorState.map(siteError => (
              <React.Fragment key={`hccvq_indoor_${siteError['site-id']}`}>
                {/** HCCVQ室内機エラー */}
                {siteError.hccvq['indoor-unit-error-states'] !== undefined &&
                siteError.hccvq['indoor-unit-error-states'].length > 0 ? (
                  <IUErrorList
                    siteName={siteError['site-name']}
                    iuErrorList={siteError.hccvq['indoor-unit-error-states']}
                  />
                ) : (
                  <></>
                )}
              </React.Fragment>
            ))}
            {props.errorState.map(siteError => (
              <React.Fragment key={`hyozan_outdoor_${siteError['site-id']}`}>
                {/** Hyozan室外機エラー */}
                {siteError.hyozan['outdoor-unit-error-states'] !== undefined &&
                siteError.hyozan['outdoor-unit-error-states'].length > 0 ? (
                  <OUErrorList
                    siteName={siteError['site-name']}
                    ouErrorList={siteError.hyozan['outdoor-unit-error-states']}
                  />
                ) : (
                  <></>
                )}
              </React.Fragment>
            ))}
            {props.errorState.map(siteError => (
              <React.Fragment key={`hccvq_outdoor_${siteError['site-id']}`}>
                {/** HCCVQ室外機エラー */}
                {siteError.hccvq['outdoor-unit-error-states'] !== undefined &&
                siteError.hccvq['outdoor-unit-error-states'].length > 0 ? (
                  <OUErrorList
                    siteName={siteError['site-name']}
                    ouErrorList={siteError.hccvq['outdoor-unit-error-states']}
                  />
                ) : (
                  <></>
                )}
              </React.Fragment>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};
export {HeaderErrorList};
